import React, { FunctionComponent } from "react";
import {useMutation} from '@apollo/client/react';
import { gql, useQuery } from "@shared/utils";
import { Autocomplete, Button, TextField, Tooltip } from "@mui/material";

const ALL_PRODUCT_CONFIGURATIONS = gql`
  query ProductConfigurations($productId: String!) {
    productConfigurations(name: "", product: $productId , allowChildren : true ) {
      _id
      name
      hidden
      parentProductConfigurationId
    }
  }
`;
type AllProductConfigurationss = {
  productConfigurations?: Array<{
    _id: string;
    name: string;
    hidden?: boolean;
    parentProductConfigurationId? : string
  }>;
};

const SINGLE_PRODUCT_CONFIGURATION = gql`
  query ProductConfiguration($id: ID!) {
    productConfiguration(id: $id) {
      _id
      name
      hidden
    }
  }
`;

const CLONE_PRODUCT_CONFIGURATION = gql`
  mutation CloneProductConfiguration($id: String!, $name: String!) {
    cloneProductConfiguration(input: { id: $id, name: $name, hidden: true }) {
      _id
      name
      hidden
    }
  }
`;

const CREATE_PRODUCT_CONFIGURATION = gql`
  mutation CreateProductConfiguration($name: String!, $product: String!) {
    createProductConfiguration(
      input: { name: $name, product: $product, hidden: true }
    ) {
      _id
      name
      hidden
    }
  }
`;

export const ProductConfigurationSelect: FunctionComponent<{
  productId: string | null;
  disabled?: boolean;
  productConfigurationId: string | null;
  name: string;
  parentProductConfigurationId?: string;
  onChange: (productConfigurationId: string | null) => void;
}> = ({ productId, productConfigurationId, onChange, name , disabled , parentProductConfigurationId }) => {
  const normalizedProductId =
    productId && productId.length > 0 ? productId : null;
  const normalizedConfigId =
    productConfigurationId && productConfigurationId.length > 0
      ? productConfigurationId
      : null;
  const ap = useQuery<AllProductConfigurationss>(ALL_PRODUCT_CONFIGURATIONS, {
    variables: { productId: normalizedProductId! },
    skip: !normalizedProductId,
  });
  const single = useQuery<{
    productConfiguration: { _id: string; name: string; hidden?: boolean  };
  }>(SINGLE_PRODUCT_CONFIGURATION, {
    variables: { id: normalizedConfigId as string },
    skip: !normalizedConfigId,
  });

  const options =
    ap.data?.productConfigurations?.filter((p => {
      if( !parentProductConfigurationId ){
        return true;
      }
      return p.parentProductConfigurationId === parentProductConfigurationId;
    })).map((p) => {
      return {
        value: p._id,
        label: p.name,
        hidden: p.hidden,
      };
    }) || [];
  let option = options.find((o) => o.value === normalizedConfigId) ?? null;
  if (single.data?.productConfiguration && !option) {
    option = {
      value: single.data.productConfiguration._id,
      label: single.data.productConfiguration.name,
      hidden: single.data.productConfiguration.hidden,
    };
    options.push(option);
  }
  const [createProductConfiguration] = useMutation<{ createProductConfiguration: { _id: string } }>(
    CREATE_PRODUCT_CONFIGURATION
  );
  const [cloneProductConfiguration] = useMutation<{ cloneProductConfiguration: { _id: string } }>(CLONE_PRODUCT_CONFIGURATION);
  if (ap.loading || (normalizedConfigId && !option && single.loading)) {
    return <>Loading...</>;
  }

  return (
    <Autocomplete
    disabled={disabled}
      sx={{ marginTop: 2, marginBottom: 2 }}
      value={option}
      isOptionEqualToValue={(a, b) => a?.value === b?.value}
      onChange={(event: any, item: any) => {
        onChange(item?.value ?? null);
      }}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Product Configuration"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {option && !option?.hidden && (
                  <Tooltip title="Creates a copy of this Product Configuration so options can be edited without effecting the original configuration">
                    <span>
                      <Button
                        variant="outlined"
                        disabled={disabled}
                        onClick={() => {
                          cloneProductConfiguration({
                            variables: {
                              id: normalizedConfigId,
                              name: `Clone of ${option?.label}`,
                            },
                          }).then(({ data }) => {
                            if (data) onChange(data.cloneProductConfiguration._id);
                          });
                        }}
                      >
                        Clone
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {!normalizedConfigId && (
                  <Button
                    variant="outlined"
                    disabled={disabled}
                    onClick={() => {
                      createProductConfiguration({
                        variables: {
                          product: normalizedProductId,
                          name: `Config for ${name}`,
                        },
                      }).then(({ data }) => {
                        if (data) onChange(data.createProductConfiguration._id);
                      });
                    }}
                  >
                    New
                  </Button>
                )}
              </>
            ),
          }}
        />
      )}
    />
  );
};
